'use strict';

import {
  checkDevice,
  listEvowalks,
  listGopros,
} from './recordingAPI';
import {
  compareConnectionCounts,
  connectedDeviceNames,
  countConnectedDevices,
  isDeviceConnected,
  loadExpectedConnections,
} from './deviceConnectionMemory';
import { evowalkDevicesFromList } from './evowalkDevice';

const STEP_DELAY_MS = 450;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatListError(listError) {
  if (!listError) return null;
  if (typeof listError === 'object' && listError.message) {
    return String(listError.message);
  }
  return String(listError);
}

function devicesFromListResponse(res, kind) {
  if (!res || res.status !== 'success') return [];
  if (kind === 'gopro') {
    if (res.result && typeof res.result === 'object' && !Array.isArray(res.result)) {
      return Object.values(res.result);
    }
    if (Array.isArray(res.result)) return res.result;
    return [];
  }
  return evowalkDevicesFromList(res);
}

function findDeviceByName(devices, name) {
  const lower = String(name).toLowerCase();
  return devices.find((device) => {
    if (!device || typeof device !== 'object') return false;
    const fields = [device.address, device.name, device.advertised_name];
    return fields.some((value) => typeof value === 'string' && value.trim().toLowerCase() === lower);
  }) || null;
}

function cloneRows(rows) {
  return rows.map((row) => ({ ...row }));
}

/**
 * Build the camera / EvoWalk status rows shown in the Record page modal.
 * Remembered connection counts drive the N in "GoPro k/N".
 */
export function buildConnectionRows(expected, evowalkEnabled) {
  const rows = [];
  if (expected.hasCamerasMemory && (expected.cameras.names.length || expected.cameras.count > 0)) {
    const names = expected.cameras.names.length
      ? expected.cameras.names
      : Array.from({ length: expected.cameras.count }, (_, i) => `camera-${i + 1}`);
    rows.push({
      kind: 'camera',
      title: 'GoPro',
      current: 0,
      total: Math.max(expected.cameras.count, names.length, 1),
      names,
      status: 'pending', // pending | checking | success | failed
    });
  } else {
    rows.push({
      kind: 'camera',
      title: 'GoPro',
      current: 0,
      total: 1,
      names: [],
      status: 'pending',
    });
  }

  if (evowalkEnabled) {
    if (expected.hasEvowalksMemory && (expected.evowalks.names.length || expected.evowalks.count > 0)) {
      const names = expected.evowalks.names.length
        ? expected.evowalks.names
        : Array.from({ length: expected.evowalks.count }, (_, i) => `evowalk-${i + 1}`);
      rows.push({
        kind: 'evowalk',
        title: 'EvoWalk™',
        current: 0,
        total: Math.max(expected.evowalks.count, names.length, 1),
        names,
        status: 'pending',
      });
    } else {
      rows.push({
        kind: 'evowalk',
        title: 'EvoWalk™',
        current: 0,
        total: 1,
        names: [],
        status: 'pending',
      });
    }
  }

  return rows;
}

function emit(onProgress, phase, rows, extra = {}) {
  try {
    onProgress({
      phase,
      rows: cloneRows(rows),
      ...extra,
    });
  } catch (err) {
    // Progress UI must not abort the check (would skip list-evowalk / /check).
    console.error('[runRecordDeviceConnectionCheck] onProgress error:', err);
  }
}

/**
 * Progressive connection check for the Record page modal.
 *
 * Order (cameras first):
 * 1. list-gopros
 * 2. start list-evowalk (do not await) + animate GoPro 0/N → N/N
 * 3. await list-evowalk, then /check (RPi /check issues EvoWalk "q" commands)
 * 4. animate EvoWalk™ 0/N → N/N
 */
export async function runRecordDeviceConnectionCheck({
  evowalkEnabled = false,
  onProgress = () => {},
  delayMs = STEP_DELAY_MS,
} = {}) {
  const expected = loadExpectedConnections();
  const rows = buildConnectionRows(expected, evowalkEnabled);

  const cameraRow = rows.find((row) => row.kind === 'camera');
  const evowalkRow = rows.find((row) => row.kind === 'evowalk');

  cameraRow.status = 'checking';
  emit(onProgress, 'running', rows);

  let cameras = [];
  let evowalks = [];
  let goproError = null;
  let evoError = null;
  let checkError = null;
  let checkRes = null;
  let checkFailed = false;

  async function runGroup(row, pool, groupError) {
    row.status = 'checking';
    row.current = 0;
    emit(onProgress, 'running', rows);
    if (delayMs > 0) {
      await sleep(delayMs);
    }

    let found = 0;
    let allOk = true;
    if (row.names.length) {
      for (let i = 0; i < row.names.length; i += 1) {
        const device = findDeviceByName(pool, row.names[i]);
        if (isDeviceConnected(device)) {
          found += 1;
        } else {
          allOk = false;
        }
        // Count only devices that are actually connected so a failed group
        // never ends as N/N with a red X (e.g. EvoWalk™ 2/2 when 0 were live).
        row.current = found;
        emit(onProgress, 'running', rows);
        if (delayMs > 0) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(delayMs);
        }
      }
    } else {
      // No remembered names: step 0 → live count so the UI still shows motion.
      const live = countConnectedDevices(pool);
      const target = Math.min(Math.max(live, 0), row.total);
      found = target;
      allOk = true;
      for (let i = 1; i <= target; i += 1) {
        row.current = i;
        emit(onProgress, 'running', rows);
        if (delayMs > 0) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(delayMs);
        }
      }
      if (target === 0) {
        row.current = 0;
        emit(onProgress, 'running', rows);
        if (delayMs > 0) {
          await sleep(delayMs);
        }
      }
    }
    row.current = Math.min(found, row.total);
    const groupOk = allOk && (row.names.length ? found >= row.total : true);
    // Pass/fail for this group only — do not wait on the other device family here.
    if (groupOk && !checkFailed && !groupError) {
      row.status = 'success';
    } else {
      row.status = 'failed';
    }
    emit(onProgress, 'running', rows);
    if (delayMs > 0) {
      await sleep(Math.min(delayMs, 300));
    }
    return groupOk;
  }

  // ---- Phase 1: list-gopros, then animate cameras ----
  // Do not call /check yet — it issues EvoWalk "q" commands and would block the UI.
  let goproRes = null;
  try {
    goproRes = await listGopros();
  } catch (err) {
    goproError = err;
    goproRes = null;
  }

  cameras = devicesFromListResponse(goproRes, 'gopro');
  if (!expected.hasCamerasMemory) {
    cameraRow.total = Math.max(countConnectedDevices(cameras), 1);
  }

  // Kick off list-evowalk as soon as cameras are listed so it is not skipped if
  // the GoPro count animation is long — still do not await it before animating.
  let evoPromise = null;
  if (evowalkEnabled) {
    evoPromise = listEvowalks()
      .then((res) => res)
      .catch((err) => {
        evoError = err;
        return null;
      });
  }

  const camerasOk = await runGroup(cameraRow, cameras, goproError);

  // ---- Phase 2: await EvoWalk list, then /check, then animate EvoWalk ----
  let evowalksOk = true;
  if (evowalkEnabled) {
    evowalkRow.status = 'checking';
    emit(onProgress, 'running', rows);

    const evoRes = await evoPromise;

    try {
      checkRes = await checkDevice();
      checkFailed = !checkRes || checkRes.status !== 'success';
    } catch (err) {
      checkFailed = true;
      checkError = err;
    }

    evowalks = devicesFromListResponse(evoRes, 'evowalk');
    if (!expected.hasEvowalksMemory) {
      evowalkRow.total = Math.max(countConnectedDevices(evowalks), 1);
    }
    evowalksOk = await runGroup(evowalkRow, evowalks, evoError || checkError);
  } else {
    // Cameras-only: still validate with /check after the GoPro count finishes.
    try {
      checkRes = await checkDevice();
      checkFailed = !checkRes || checkRes.status !== 'success';
    } catch (err) {
      checkFailed = true;
      checkError = err;
    }
    if (checkFailed || goproError) {
      cameraRow.status = 'failed';
      emit(onProgress, 'running', rows);
    }
  }

  const listError = goproError || evoError || checkError;

  const comparison = compareConnectionCounts(
    expected,
    cameras,
    evowalks,
    evowalkEnabled
  );

  const success =
    !checkFailed &&
    !listError &&
    !comparison.hasMismatch &&
    camerasOk &&
    evowalksOk;

  const cameraSuccess =
    camerasOk && !comparison.camerasMismatch && !checkFailed && !goproError && !checkError;
  cameraRow.status = cameraSuccess ? 'success' : 'failed';
  cameraRow.current = cameraSuccess
    ? cameraRow.total
    : Math.min(comparison.cameraCount, cameraRow.total);

  if (evowalkRow) {
    const evowalkSuccess =
      evowalksOk && !comparison.evowalksMismatch && !checkFailed && !evoError && !checkError;
    evowalkRow.status = evowalkSuccess ? 'success' : 'failed';
    evowalkRow.current = evowalkSuccess
      ? evowalkRow.total
      : Math.min(comparison.evowalkCount, evowalkRow.total);
  }

  // Emit final rows while still "running" so RecordPage applies green checks,
  // then emit the terminal phase for the success / ready message.
  emit(onProgress, 'running', rows);
  emit(onProgress, success ? 'success' : 'error', rows);

  return {
    success,
    checkFailed,
    checkRes,
    listError: formatListError(listError),
    comparison,
    expected,
    cameras,
    evowalks,
    connectedCameraNames: connectedDeviceNames(cameras),
    connectedEvowalkNames: connectedDeviceNames(evowalks),
    evowalkEnabled,
    rows: cloneRows(rows),
  };
}
